import { Component } from '@theme/component';

/**
 * Ola · Nuestra tienda, diseños nuevos (sections/ola-tienda.liquid).
 *
 * - «Copiar dirección»: llega oculto y se muestra solo si el navegador puede copiar. Al tocarlo
 *   copia `data-text`, cambia el texto a `data-label-done` por unos segundos y lo anuncia en la
 *   región `copyStatus`.
 * - Pin propio: va al centro del mapa de Google. Cuando la persona toca el mapa (la ventana pierde
 *   el foco y el iframe lo gana), marca `data-map-active` para ocultarlo, porque al mover el mapa
 *   el pin propio ya no estaría en su lugar; queda el pin de Google.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [copyButton]
 * @property {HTMLElement} [copyLabel]
 * @property {HTMLElement} [copyStatus]
 * @property {HTMLIFrameElement} [mapFrame]
 *
 * @extends {Component<Refs>}
 */
export class OlaTiendaPanel extends Component {
  /** Tiempo que se ve «Dirección copiada», en milisegundos. */
  static COPIED_FOR = 2500;

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    window.clearTimeout(this.#timer);
  }

  /** Copia la dirección de la tienda. */
  async copyAddress() {
    const { copyButton, copyLabel, copyStatus } = this.refs;
    const text = copyButton?.dataset.text;
    if (!copyButton || !text) return;

    try {
      await navigator.clipboard.writeText(text);
    } catch {
      return;
    }

    const done = copyButton.dataset.labelDone ?? '';
    if (copyLabel) copyLabel.textContent = done;
    if (copyStatus) copyStatus.textContent = done;

    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      if (copyLabel) copyLabel.textContent = copyButton.dataset.labelIdle ?? '';
      if (copyStatus) copyStatus.textContent = '';
    }, OlaTiendaPanel.COPIED_FOR);
  }

  #setup() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const { copyButton, mapFrame } = this.refs;
    if (copyButton) copyButton.hidden = typeof navigator.clipboard?.writeText !== 'function';

    if (mapFrame) {
      window.addEventListener(
        'blur',
        () => {
          // El foco pasa al iframe justo después del blur.
          window.setTimeout(() => {
            if (document.activeElement === mapFrame) this.setAttribute('data-map-active', '');
          }, 0);
        },
        { signal }
      );
    }
  }
}

if (!customElements.get('ola-tienda-panel')) {
  customElements.define('ola-tienda-panel', OlaTiendaPanel);
}
